import { keepPreviousData } from '@tanstack/react-query'
import { decode } from 'html-entities'

import type {
  BidListItem,
  AccountUniqueBids,
  GetAuctionsResponse,
  AuctionListItems,
} from '@b-stock/auction-api-client'
import { canReadAsBuyer } from '@b-stock/fe-core/bstock-next/helpers'
import {
  getBidsClient,
  makeOptionallyAuthenticatedQuery,
  fetchAllResultsById,
  getAuctionsClient,
  getOfferingsClient,
  BidStatus,
} from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type { OfferingDto } from '@b-stock/offering-api-client'
import type {
  AllListingsListing,
  AllListings200Response,
  AllListingsApiAllListingsRequest,
  AllListingsFilters,
  GetAllListingsDto,
} from '@b-stock/search-api-client'
import { AllListingsSortOrderEnum } from '@b-stock/search-api-client'

import getSearchFilters from '@/helpers/api/getSearchFilters'
import { getEndTime } from '@/helpers/listing/getEndTime'
import getEntAuctions from '@/queries/getEntAuctions'
import type { BridgeEntAuction } from '@/queries/getEntAuctions'

import getAllListingsSearchClient from './getAllListingsSearchClient'
import type { WatchlistStatus } from './watchlistQuery'

// TODO (BP-853): Remove this type extension when backend supports deals filter
// Extends API types to include deals filter for type safety
type ExtendedAllListingsFilters = AllListingsFilters & {
  deals?: Array<{ value: string; count?: number }>
}

export type AuctionFrom3MP = AuctionListItems & {
  bid: BidListItem | null
  bidStatus: BidStatus
}

export type AuctionFromEnt = BridgeEntAuction & {
  bidStatus: BidStatus
}

export type SearchResult = AllListingsListing & {
  retailPrice?: number | null
  auction3MP: AuctionFrom3MP | null
  auctionEnt: AuctionFromEnt | null
  offering?: OfferingDto | null
  watchlistStatus?: WatchlistStatus
}

export type SearchResultsPage = Omit<AllListings200Response, 'listings'> & {
  listings: SearchResult[]
  allFilters: ExtendedAllListingsFilters
}

/** Minimal shape shared by SearchResultsPage and SimilarSearchResult. */
export type SearchPage = {
  listings: SearchResult[]
}

export type SearchParams = {
  params: AllListingsApiAllListingsRequest & {
    query?: string | null
  }
  fetchFilters?: boolean
  usePostEndpoint?: boolean
}

const getBidsQueryParameters = (ids: string[], limit: number) => ({
  auctionId: ids.join(','),
  limit,
})

const getAuctionsQueryParameters = (ids: string[], limit: number) => ({
  listingId: ids.join(','),
  limit: limit.toString(),
})

const bidsResponseToResults = (response: AccountUniqueBids) => response.bids
const auctionsResponseToResults = (response: GetAuctionsResponse) =>
  response.auctions

const getBidStatus3MP = (bid: BidListItem | null): BidStatus => {
  if (!bid) return BidStatus.NO_BID

  if (bid.auction.closed) {
    return bid.winning ? BidStatus.WON : BidStatus.LOST
  } else {
    return bid.winning ? BidStatus.WINNING : BidStatus.LOSING
  }
}

const getBidStatusEnt = (auction: BridgeEntAuction): BidStatus => {
  const done = auction.closed || auction.canceled

  if (auction.buyerBidStatus === 'HAS_NOT_BID') {
    return BidStatus.NO_BID
  } else if (auction.buyerBidStatus === 'WINNING') {
    return done ? BidStatus.WON : BidStatus.WINNING
  } else {
    return done ? BidStatus.LOST : BidStatus.LOSING
  }
}

const emptyListOnRejection = async <T>(
  promise: Promise<T[]> | T[]
): Promise<T[]> => {
  try {
    return await promise
  } catch {
    return []
  }
}

export const sortListingsByEndTime = (
  listings: SearchResult[],
  order: AllListingsSortOrderEnum = AllListingsSortOrderEnum.Asc
): SearchResult[] =>
  [...listings].sort(
    (a, b) =>
      (getEndTime(a).getTime() - getEndTime(b).getTime()) *
      (order === AllListingsSortOrderEnum.Asc ? 1 : -1)
  )

// The search API doesn't preserve the order of the requested `id` list, so
// callers relying on that order (e.g. recently-viewed, which is LIFO) must
// re-sort the results themselves.
export const sortListingsByIdOrder = (
  listings: SearchResult[],
  idOrder: string[]
): SearchResult[] => {
  const indexById = new Map(idOrder.map((id, index) => [id, index]))
  // Listings missing from idOrder (shouldn't normally happen) sort after all
  // tracked ones instead of colliding with index 0.
  const indexOf = (id: string) => indexById.get(id) ?? idOrder.length
  return [...listings].sort((a, b) => indexOf(a.id) - indexOf(b.id))
}

export const augmentListings = async (
  accessToken: string | null,
  listings: AllListingsListing[],
  axiosParams: ReturnType<typeof buildAxiosRequestConfig>
): Promise<SearchResult[]> => {
  const bidsClient = getBidsClient()
  const auctionsClient = getAuctionsClient()
  const offeringsClient = getOfferingsClient()

  const auctionListingIds3MP: string[] = []
  const auctionIds3MP: string[] = []
  const auctionIdsEnt: string[] = []
  const maoListingIds: string[] = []

  listings.forEach(
    ({ siteName, listingId, auctionId, siteAbb, pricingStrategy }) => {
      if (siteName === 'TMP' && listingId) {
        auctionListingIds3MP.push(listingId)
        if (auctionId) auctionIds3MP.push(auctionId)
      } else if (listingId) {
        auctionIdsEnt.push(siteAbb + listingId)
      }
      if (pricingStrategy === 'MAKE_AN_OFFER' && listingId) {
        maoListingIds.push(listingId)
      }
    }
  )

  const auctions3MPByListingId: Partial<Record<string, AuctionListItems>> = {}
  const auctionsEntByAuctionId: Partial<Record<string, BridgeEntAuction>> = {}
  const bidsByAuctionId: Partial<Record<string, BidListItem>> = {}
  const offeringsByListingId: Partial<Record<string, OfferingDto>> = {}

  const [auctions3MP, bids3MP, auctionsEnt, offerings] = await Promise.all([
    emptyListOnRejection(
      fetchAllResultsById({
        apiClient: auctionsClient,
        query: auctionsClient.getAuctions,
        ids: auctionListingIds3MP,
        getQueryParameters: getAuctionsQueryParameters,
        responseToResults: auctionsResponseToResults,
        axiosOptions: axiosParams,
      })
    ),
    // If user is a buyer, also query for bids
    canReadAsBuyer(accessToken)
      ? emptyListOnRejection(
          fetchAllResultsById({
            apiClient: bidsClient,
            query: bidsClient.getAccountUniqueBids,
            ids: auctionIds3MP,
            getQueryParameters: getBidsQueryParameters,
            responseToResults: bidsResponseToResults,
            axiosOptions: axiosParams,
            limit: 100,
          })
        )
      : [],
    // This call is known to fail if queried for an auction ID that doesn't
    // exist (to the service's knowledge). This shouldn't really happen, but
    // we're being careful because it does happen in dev at least.
    emptyListOnRejection(getEntAuctions(auctionIdsEnt, axiosParams)),
    // Fetch offerings for MAO listings
    emptyListOnRejection(
      maoListingIds.length > 0
        ? fetchAllResultsById({
            apiClient: offeringsClient,
            query: offeringsClient.findOfferings,
            ids: maoListingIds,
            getQueryParameters: (ids, limit) => ({
              listingId: ids,
              limit,
            }),
            responseToResults: (response) => response.offerings,
            axiosOptions: axiosParams,
            limit: 100,
          })
        : []
    ),
  ])

  auctions3MP.forEach((auction) => {
    if (auction._id) {
      auctions3MPByListingId[auction.listingId] = auction
    }
  })

  bids3MP.forEach((bid) => {
    if (bid.auctionId) {
      bidsByAuctionId[bid.auctionId] = bid
    }
  })

  auctionsEnt.forEach((auction) => {
    auctionsEntByAuctionId[auction.auctionId] = auction
  })

  offerings.forEach((offering: OfferingDto) => {
    if (offering.listingId) {
      offeringsByListingId[offering.listingId] = offering
    }
  })

  return listings.map((result: AllListingsListing) => {
    const auction3MP = auctions3MPByListingId[result.id || ''] || null
    const bid = (auction3MP && bidsByAuctionId[auction3MP._id]) ?? null
    const auctionEnt = result.listingId
      ? auctionsEntByAuctionId[result.siteAbb + result.listingId]
      : null
    const offering = result.listingId
      ? offeringsByListingId[result.listingId]
      : undefined

    let title = result.title
    if (result.siteName !== 'TMP') {
      // `TMP` = 3mp
      // Enterprise listing titles go through an escaping pass before being saved to
      // the DB. This is strictly speaking wrong, but it's how the old system works.
      // At the point that we don't need to support listings from the legacy system
      // anymore, this can and should be removed.
      title = decode(title)
    }
    return {
      ...result,
      title,
      auction3MP: auction3MP
        ? {
            ...auction3MP,
            bid,
            bidStatus: getBidStatus3MP(bid),
          }
        : null,
      auctionEnt: auctionEnt
        ? {
            ...auctionEnt,
            bidStatus: getBidStatusEnt(auctionEnt),
          }
        : null,
      offering: offering || null,
    }
  })
}

/**
 * Raw plain-search fetch (exact filter match, unaugmented) — the
 * counterpart to similarSearchQuery's fetchSimilarListings. Exists so a
 * caller fanning out across many seeds (recentViewsSeedCandidatesQuery) can
 * merge raw candidates from both searches and dedupe before paying for
 * augmentListings once, instead of augmenting per seed per search type.
 */
export const fetchListings = async (
  params: AllListingsApiAllListingsRequest,
  axiosParams: ReturnType<typeof buildAxiosRequestConfig>
): Promise<AllListingsListing[]> => {
  try {
    const { data } = await getAllListingsSearchClient().allListings(
      params,
      axiosParams
    )
    return data.listings
  } catch {
    return []
  }
}

// Drives ALL AUCTIONS, Seller Auctions, and watchlist pages
export const getSearchData = async (
  accessToken: string | null,
  { params, fetchFilters = true, usePostEndpoint = false }: SearchParams
): Promise<SearchResultsPage> => {
  const searchClient = getAllListingsSearchClient()
  const axiosParams = buildAxiosRequestConfig({ accessToken })

  const { query, ...searchParams } = params

  const getAllListingsDto: GetAllListingsDto = {
    ...searchParams,
    // Paper over 'deals' type mismatch. Cast string to enum.
    deals: params.deals,
  }

  // choose whether to use the POST or GET endpoint for all listings
  const allListingsRequest = usePostEndpoint
    ? searchClient.allListingsPost({ getAllListingsDto }, axiosParams)
    : searchClient.allListings(searchParams, axiosParams)

  const [{ data: searchResponse }, filtersResponse] = await Promise.all([
    allListingsRequest,
    fetchFilters
      ? getSearchFilters({ accessToken, storeFrontId: params.storeFrontId })
      : {},
  ])

  const augmentedResult = await augmentListings(
    accessToken,
    searchResponse.listings,
    axiosParams
  )

  return {
    listings:
      params.sortBy === 'endTime'
        ? sortListingsByEndTime(augmentedResult, params.sortOrder)
        : augmentedResult,
    total: searchResponse.total,
    limit: searchResponse.limit,
    offset: searchResponse.offset,
    allFilters: filtersResponse,
    ...(searchResponse.personalizationNotFound && {
      personalizationNotFound: true,
    }),
    ...(searchResponse.modelVersion && {
      modelVersion: searchResponse.modelVersion,
    }),
  }
}

const listingSearchQuery = makeOptionallyAuthenticatedQuery<
  [SearchParams],
  SearchResultsPage
>('search', getSearchData, {
  placeholderData: keepPreviousData,
  staleTime: 5 * 60 * 1000, // 5 minutes
})

export default listingSearchQuery
